import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';

/*
 * Read layer for the production domain (ops merge). Endpoints are
 * global-admin-gated and not org-scoped — productions span the whole
 * company. Ids arrive as strings (BIGSERIAL over the wire); coerce
 * with + where needed.
 */

export default function useProduction() {
    const authenticatedApi = useAuthenticatedApi();

    const getProductions = async (params?: {
        status?: string;
        search?: string;
        jobId?: number;
    }) => {
        const response = await authenticatedApi.get('/production/activations', {
            params,
        });
        return response.data;
    };

    const getProductionRecord = async (activationId: number) => {
        const response = await authenticatedApi.get(
            `/production/activations/${activationId}`,
        );
        return response.data;
    };

    const getStaff = async (search?: string) => {
        const response = await authenticatedApi.get('/production/staff', {
            params: search ? { search } : undefined,
        });
        return response.data;
    };

    const getStaffMember = async (staffId: number) => {
        const response = await authenticatedApi.get(
            `/production/staff/${staffId}`,
        );
        return response.data;
    };

    const getVenues = async (search?: string) => {
        const response = await authenticatedApi.get('/production/venues', {
            params: search ? { search } : undefined,
        });
        return response.data;
    };

    const getVenue = async (venueId: number) => {
        const response = await authenticatedApi.get(
            `/production/venues/${venueId}`,
        );
        return response.data;
    };

    const getGear = async (search?: string) => {
        const response = await authenticatedApi.get('/production/gear', {
            params: search ? { search } : undefined,
        });
        return response.data;
    };

    const getDesignRequests = async (status?: string) => {
        const response = await authenticatedApi.get(
            '/production/design-requests',
            { params: status ? { status } : undefined },
        );
        return response.data;
    };

    const getContacts = async (search?: string) => {
        const response = await authenticatedApi.get('/production/contacts', {
            params: search ? { search } : undefined,
        });
        return response.data;
    };

    const getContact = async (contactId: number) => {
        const response = await authenticatedApi.get(
            `/production/contacts/${contactId}`,
        );
        return response.data;
    };

    const getCompanies = async (search?: string) => {
        const response = await authenticatedApi.get('/production/companies', {
            params: search ? { search } : undefined,
        });
        return response.data;
    };

    /* ---- writes ---- */

    const createProduction = async (fields: Record<string, any>) => {
        const response = await authenticatedApi.post(
            '/production/activations',
            fields,
        );
        return response.data;
    };

    const updateProduction = async (
        activationId: number,
        fields: Record<string, any>,
    ) => {
        const response = await authenticatedApi.put(
            `/production/activations/${activationId}`,
            fields,
        );
        return response.data;
    };

    const updateProductionStatus = async (
        activationId: number,
        status: string,
    ) => {
        const response = await authenticatedApi.patch(
            `/production/activations/${activationId}/status`,
            { status },
        );
        return response.data;
    };

    const duplicateProduction = async (activationId: number) => {
        const response = await authenticatedApi.post(
            `/production/activations/${activationId}/duplicate`,
        );
        return response.data;
    };

    const deleteProduction = async (activationId: number) => {
        const response = await authenticatedApi.delete(
            `/production/activations/${activationId}`,
        );
        return response.data;
    };

    const addStaffAssignment = async (
        activationId: number,
        fields: Record<string, any>,
    ) => {
        const response = await authenticatedApi.post(
            `/production/activations/${activationId}/staff-assignments`,
            fields,
        );
        return response.data;
    };

    const setStaffAssignmentStatus = async (
        assignmentId: number,
        status: string,
    ) => {
        const response = await authenticatedApi.patch(
            `/production/staff-assignments/${assignmentId}/status`,
            { status },
        );
        return response.data;
    };

    const removeStaffAssignment = async (assignmentId: number) => {
        const response = await authenticatedApi.delete(
            `/production/staff-assignments/${assignmentId}`,
        );
        return response.data;
    };

    const addGearAssignment = async (
        activationId: number,
        fields: Record<string, any>,
    ) => {
        const response = await authenticatedApi.post(
            `/production/activations/${activationId}/gear-assignments`,
            fields,
        );
        return response.data;
    };

    const removeGearAssignment = async (assignmentId: number) => {
        const response = await authenticatedApi.delete(
            `/production/gear-assignments/${assignmentId}`,
        );
        return response.data;
    };

    const addActivationContact = async (
        activationId: number,
        fields: { contactId: number; role: string; notes?: string },
    ) => {
        const response = await authenticatedApi.post(
            `/production/activations/${activationId}/contacts`,
            fields,
        );
        return response.data;
    };

    const removeActivationContact = async (activationContactId: number) => {
        const response = await authenticatedApi.delete(
            `/production/activation-contacts/${activationContactId}`,
        );
        return response.data;
    };

    const createStaffMember = async (fields: Record<string, any>) => {
        const response = await authenticatedApi.post(
            '/production/staff',
            fields,
        );
        return response.data;
    };

    const updateStaffMember = async (
        staffId: number,
        fields: Record<string, any>,
    ) => {
        const response = await authenticatedApi.put(
            `/production/staff/${staffId}`,
            fields,
        );
        return response.data;
    };

    const deleteStaffMember = async (staffId: number) => {
        const response = await authenticatedApi.delete(
            `/production/staff/${staffId}`,
        );
        return response.data;
    };

    const bulkCreateStaff = async (rows: Record<string, any>[]) => {
        const response = await authenticatedApi.post('/production/staff/bulk', {
            rows,
        });
        return response.data;
    };

    const createVenue = async (fields: Record<string, any>) => {
        const response = await authenticatedApi.post(
            '/production/venues',
            fields,
        );
        return response.data;
    };

    const updateVenue = async (
        venueId: number,
        fields: Record<string, any>,
    ) => {
        const response = await authenticatedApi.put(
            `/production/venues/${venueId}`,
            fields,
        );
        return response.data;
    };

    const deleteVenue = async (venueId: number) => {
        const response = await authenticatedApi.delete(
            `/production/venues/${venueId}`,
        );
        return response.data;
    };

    const createGearAsset = async (fields: Record<string, any>) => {
        const response = await authenticatedApi.post(
            '/production/gear',
            fields,
        );
        return response.data;
    };

    const updateGearAsset = async (
        gearAssetId: number,
        fields: Record<string, any>,
    ) => {
        const response = await authenticatedApi.put(
            `/production/gear/${gearAssetId}`,
            fields,
        );
        return response.data;
    };

    const deleteGearAsset = async (gearAssetId: number) => {
        const response = await authenticatedApi.delete(
            `/production/gear/${gearAssetId}`,
        );
        return response.data;
    };

    const createDesignRequest = async (fields: Record<string, any>) => {
        const response = await authenticatedApi.post(
            '/production/design-requests',
            fields,
        );
        return response.data;
    };

    const updateDesignRequest = async (
        requestId: number,
        fields: Record<string, any>,
    ) => {
        const response = await authenticatedApi.put(
            `/production/design-requests/${requestId}`,
            fields,
        );
        return response.data;
    };

    const createDeliverable = async (
        requestId: number,
        fields: Record<string, any>,
    ) => {
        const response = await authenticatedApi.post(
            `/production/design-requests/${requestId}/deliverables`,
            fields,
        );
        return response.data;
    };

    const updateDeliverable = async (
        deliverableId: number,
        fields: Record<string, any>,
    ) => {
        const response = await authenticatedApi.put(
            `/production/deliverables/${deliverableId}`,
            fields,
        );
        return response.data;
    };

    const createContact = async (fields: Record<string, any>) => {
        const response = await authenticatedApi.post(
            '/production/contacts',
            fields,
        );
        return response.data;
    };

    const updateContact = async (
        contactId: number,
        fields: Record<string, any>,
    ) => {
        const response = await authenticatedApi.put(
            `/production/contacts/${contactId}`,
            fields,
        );
        return response.data;
    };

    const getJobs = async () => {
        const response = await authenticatedApi.get('/production/jobs');
        return response.data;
    };

    const getJob = async (jobId: number) => {
        const response = await authenticatedApi.get(`/production/jobs/${jobId}`);
        return response.data;
    };

    const createJob = async (fields: Record<string, any>) => {
        const response = await authenticatedApi.post('/production/jobs', fields);
        return response.data;
    };

    const updateJob = async (jobId: number, fields: Record<string, any>) => {
        const response = await authenticatedApi.put(
            `/production/jobs/${jobId}`,
            fields,
        );
        return response.data;
    };

    const addJobActivation = async (jobId: number) => {
        const response = await authenticatedApi.post(
            `/production/jobs/${jobId}/activations`,
        );
        return response.data;
    };

    const enableOps = async (activationId: number) => {
        const response = await authenticatedApi.post(
            `/production/activations/${activationId}/enable-ops`,
        );
        return response.data;
    };

    const getCalendarFeeds = async () => {
        const response = await authenticatedApi.get(
            '/production/calendar-feeds',
        );
        return response.data;
    };

    const getTeamCalendarFeed = async () => {
        const response = await authenticatedApi.post(
            '/production/calendar-feeds/team',
        );
        return response.data;
    };

    const getStaffCalendarFeed = async (staffId: number) => {
        const response = await authenticatedApi.post(
            `/production/calendar-feeds/staff/${staffId}`,
        );
        return response.data;
    };

    const revokeCalendarFeed = async (feedId: number) => {
        const response = await authenticatedApi.delete(
            `/production/calendar-feeds/${feedId}`,
        );
        return response.data;
    };

    const getIntegrationsStatus = async () => {
        const response = await authenticatedApi.get('/production/integrations');
        return response.data;
    };

    const importContactFromAttio = async (attioId: string) => {
        const response = await authenticatedApi.post(
            '/production/contacts/import-attio',
            { attioId },
        );
        return response.data;
    };

    return {
        getJobs,
        getJob,
        createJob,
        updateJob,
        addJobActivation,
        enableOps,
        getCalendarFeeds,
        getTeamCalendarFeed,
        getStaffCalendarFeed,
        revokeCalendarFeed,
        getIntegrationsStatus,
        importContactFromAttio,
        getProductions,
        getProductionRecord,
        getStaff,
        getStaffMember,
        getVenues,
        getVenue,
        getGear,
        getDesignRequests,
        getContacts,
        getContact,
        getCompanies,
        createProduction,
        updateProduction,
        updateProductionStatus,
        duplicateProduction,
        deleteProduction,
        addStaffAssignment,
        setStaffAssignmentStatus,
        removeStaffAssignment,
        addGearAssignment,
        removeGearAssignment,
        addActivationContact,
        removeActivationContact,
        createStaffMember,
        updateStaffMember,
        deleteStaffMember,
        bulkCreateStaff,
        createVenue,
        updateVenue,
        deleteVenue,
        createGearAsset,
        updateGearAsset,
        deleteGearAsset,
        createDesignRequest,
        updateDesignRequest,
        createDeliverable,
        updateDeliverable,
        createContact,
        updateContact,
    };
}
